<template>
  <div ref="rootRef" class="full-width" :style="containerStyle">
    <slot />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import {
  getSpacingStyleData,
  getColorStyleData,
  getSizeStyleData,
} from '../../../composables/useStyleData'
import type { FullWidthContainerFields } from './definition'

const props = withDefaults(
  defineProps<{
    fields?: FullWidthContainerFields
  }>(),
  {
    fields: () => ({}),
  },
)

const containerStyle = computed(() => {
  const fields = props.fields
  const styles: Record<string, string> = {}
  const layout = fields.layout || 'block'

  if (layout !== 'block') {
    styles.display = 'flex'
    styles.flexDirection = layout
    const gap = getSpacingStyleData(fields.gap)
    if (gap) styles.gap = gap
    if (fields.alignItems) styles.alignItems = fields.alignItems
    if (fields.justifyContent) styles.justifyContent = fields.justifyContent
    if (fields.wrap) styles.flexWrap = 'wrap'
  }

  const padding = getSpacingStyleData(fields.padding)
  if (padding) styles.padding = padding
  const minHeight = getSizeStyleData(fields.minHeight)
  if (minHeight) styles.minHeight = minHeight
  const bgColor = getColorStyleData(fields.bgColor)
  if (bgColor) styles.backgroundColor = bgColor
  const borderRadius = getSizeStyleData(fields.borderRadius)
  if (borderRadius) styles.borderRadius = borderRadius

  return styles
})

const rootRef = ref<HTMLElement | null>(null)

// Read the page container's actual padding (the element directly under <body>) so the
// breakout tracks whatever the base CSS uses. getComputedStyle is zoom-immune, so
// negating it as our margin cancels the gutter exactly. CSS falls back to --space-16.
onMounted(() => {
  let pageContainer: HTMLElement | null = rootRef.value
  while (pageContainer?.parentElement && pageContainer.parentElement !== document.body) {
    pageContainer = pageContainer.parentElement
  }
  const pad = pageContainer ? parseFloat(getComputedStyle(pageContainer).paddingLeft) : 0
  if (pad > 0) rootRef.value?.style.setProperty('--page-padding', `${pad}px`)
})
</script>

<style scoped>
/* Margins are spelled per side: Safari before 15 drops a `margin-inline` whose value
   holds a `var()` (it computes to 0), which left this container one gutter off-centre
   on iOS 14. Both sides take the same value, so this is the same in RTL. */
.full-width {
  margin-left: calc(-1 * var(--page-padding, var(--space-16)));
  margin-right: calc(-1 * var(--page-padding, var(--space-16)));
}

/* Touch devices take the viewport breakout at any width, as they did when the old
   viewport meta reported the design width; viewport units are divided by the page
   zoom they sit under (render/lib/pageZoom.ts), which is 1 on desktop. */
@media (min-width: 480px), (hover: none) and (pointer: coarse) {
  .full-width {
    width: calc(100vw / var(--app-zoom, 1));
    margin-left: calc(50% - 50vw / var(--app-zoom, 1));
    margin-right: calc(50% - 50vw / var(--app-zoom, 1));
  }
}
</style>

<!-- Unscoped: reserves the desktop scrollbar width from first paint so `100vw`
     doesn't jump when a classic scrollbar appears. `:has(.full-width)` keeps
     the side effect off pages without this component. Mobile browsers use
     overlay scrollbars and ignore it, so no layout change there. -->
<style>
html:has(.full-width) {
  scrollbar-gutter: stable;
}
</style>
